/*
 * Original source https://github.com/srid/imako/tree/master
 * Commit 5ac0382b0795f0897eea866fe335586a3083bc9c
 */

import { LitElement, html, css, TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import type { OutDesignNode } from "./types";

import "./InlineRenderer";
import "../ui/OutDesignMetaElement"

export class BlockRenderer extends LitElement {
  @property({ attribute: false })
  blocks: OutDesignNode[] = [];

  static styles = css`
    :host {
      display: block; 
      word-break: break-word;
      width: calc(100% - 2rem);
      margin: 0 1rem;
    }
    
    @media (min-width: 1250px) {
      :host {
        width: calc(100% - 6rem);
        margin: 0 3rem;
      }
    }

    p, h1, h2, h3, h4, h5, h6 {
      margin-top: 0.25rem;
    }
  `;

  render() {
    return html`${this.blocks.map((b) => this.renderBlock(b))}`;
  }

  renderBlock(block: OutDesignNode) {
    switch (block.type) {
      case "Plain":
        return html`
            <inline-renderer .inlines=${block.children}></inline-renderer>
        `;

      case "Para":
        return html`
          <od-block .node=${block}>
            <p>
              <inline-renderer .inlines=${block.children}></inline-renderer>
            </p>
          </od-block>
        `;

      case "Header": {
        const level = block.data?.level;

        let header: TemplateResult = html``;

        if (level == 1)
          header = html`<h1>
            <inline-renderer .inlines=${block.children}></inline-renderer>
          </h1>`;

        if (level == 2)
          header = html`<h2>
            <inline-renderer .inlines=${block.children}></inline-renderer>
          </h2>`;

        if (level == 3)
          header = html`<h3>
            <inline-renderer .inlines=${block.children}></inline-renderer>
          </h3>`;

        if (level == 4)
          header = html`<h4>
            <inline-renderer .inlines=${block.children}></inline-renderer>
          </h4>`;

        if (level == 5)
          header = html`<h5>
            <inline-renderer .inlines=${block.children}></inline-renderer>
          </h5>`;

        if (level == 6)
          header = html`<h6>
            <inline-renderer .inlines=${block.children}></inline-renderer>
          </h6>`;

        return html`
          <od-block .node=${block} .level=${level}>
              ${header}
          </od-block>
        `;
      }

      case "BulletList":
        return html`
          <od-block .node=${block}>
            <ul>${block.children?.map((item) => this.renderListItem([item]))}</ul>
          </od-block>
        `;

      case "OrderedList": {
        return html`
          <od-block .node=${block}>
            <ol>
              ${block.children?.map(
                (item) => html`
                  <li>
                    <block-renderer .blocks=${item}></block-renderer>
                  </li>
                `
              )}
            </ol>
          </od-block>
        `;
      }

      case "CodeBlock": {
        const code = block.data?.text;

        return html`
          <od-block .node=${block}>
            <pre>${code}</pre>
          </od-block>
        `;
      }

      case "BlockQuote":
        return html`
          <od-block .node=${block}>
            <blockquote>
              <block-renderer .blocks=${block.children}></block-renderer>
            </blockquote>
          </od-block>
        `;

      case "HorizontalRule":
        return html`<hr>`;

      case "Div":
        return html`
          <od-block .node=${block}>
            <div>
              <block-renderer .blocks=${block.children}></block-renderer>
            </div>
          </od-block>
        `;

      case "LineBlock":
        return html`
          <div>
            ${block.children?.map(
              (line) => html`
                <div>
                  <inline-renderer .inlines=${line}></inline-renderer>
                </div>
              `
            )}
          </div>
        `;

      case "RawBlock": {
        const text = block.data?.text;

        return html`
          <od-block .node=${block}>
            <pre>${text}</pre>
          </od-block>
        `;
      }

      // case "DefinitionList":
      //   return html`
      //     <dl>
      //       ${block.children.map(
      //         ([term, defs]) => html`
      //           <div>
      //             <dt>
      //               <inline-renderer .inlines=${term}></inline-renderer>
      //             </dt>

      //             ${defs.map(
      //               (def) => html`
      //                 <dd>
      //                   <block-renderer .blocks=${def}></block-renderer>
      //                 </dd>
      //               `
      //             )}
      //           </div>
      //         `
      //       )}
      //     </dl>
      //   `;

      case "Figure": {
        const _attr = block.attr;
        const caption = block.data?.caption;
        const blocks = block.children;

        const captionInlines =
          caption[1].flatMap((blk) =>
            blk.t === "Plain" || blk.t === "Para" ? blk.c : []
          ) ?? [];

        return html`
          <od-block .node=${block}>
            <figure>
              <block-renderer .blocks=${blocks}></block-renderer>

              ${caption[0]
                ? html`
                    <figcaption>
                      <inline-renderer
                        .inlines=${captionInlines}
                      ></inline-renderer>
                    </figcaption>
                  `
                : ""}
            </figure>
          </od-block>
        `;
      }

      // case "Table":
      //   return this.renderTable(block);

      default:
        return html``;
    }
  }

  renderListItem(blocks: OutDesignNode[]) {
    const first = blocks[0];

    const isSinglePara =
      blocks.length === 1 && (first?.type === "Para" || first?.type === "Plain");

    const isNestedList =
      first?.type === "BulletList" || first?.type === "OrderedList";

    if (isSinglePara) {
      const inlines = (first as { children: OutDesignNode[] }).children;

      return html`
        <li><inline-renderer .inlines=${inlines}></inline-renderer></li>
      `;
    }

    if (isNestedList) {
      return html`
        <block-renderer .blocks=${blocks}></block-renderer>
      `;
    }

    return html`
      <block-renderer .blocks=${blocks}></block-renderer>
    `;
  }

  // renderTable(block: Extract<OutDesignNode, { t: "Table" }>) {
  //   const [_attr, caption, colSpecs, tableHead, tableBodies, tableFoot] =
  //     block.children;

  //   const alignClass = (align: string) => {
  //     switch (align) {
  //       case "AlignRight":
  //         return "text-right";
  //       case "AlignCenter":
  //         return "text-center";
  //       default:
  //         return "text-left";
  //     }
  //   };

  //   const renderRow = (row: [Attr, any[]]) => {
  //     const [_rowAttr, cells] = row;

  //     return html`
  //       <tr>
  //         ${cells.map((cell: any, colIdx: number) => {
  //           const [_attr, alignment, _rowSpan, _colSpan, blocks] = cell;

  //           const colAlign =
  //             alignment !== "AlignDefault"
  //               ? alignment
  //               : colSpecs[colIdx]?.[0];

  //           return html`
  //             <td class="${alignClass(colAlign)}">
  //               <block-renderer .blocks=${blocks}></block-renderer>
  //             </td>
  //           `;
  //         })}
  //       </tr>
  //     `;
  //   };

  //   return html`
  //     <div>
  //       <table>
  //         ${caption[1].length
  //           ? html`
  //               <caption>
  //                 <block-renderer .blocks=${caption[1]}></block-renderer>
  //               </caption>
  //             `
  //           : ""}

  //         ${tableHead[1].length
  //           ? html`
  //               <thead>
  //                 ${tableHead[1].map((row: any) => {
  //                   const [_attr, cells] = row;

  //                   return html`
  //                     <tr>
  //                       ${cells.map((cell: any, colIdx: number) => {
  //                         const [_a, alignment, _rs, _cs, blocks] = cell;

  //                         const colAlign =
  //                           alignment !== "AlignDefault"
  //                             ? alignment
  //                             : colSpecs[colIdx]?.[0];

  //                         return html`
  //                           <th
  //                             scope="col"
  //                             class="${alignClass(
  //                               colAlign
  //                             )}"
  //                           >
  //                             <block-renderer
  //                               .blocks=${blocks}
  //                             ></block-renderer>
  //                           </th>
  //                         `;
  //                       })}
  //                     </tr>
  //                   `;
  //                 })}
  //               </thead>
  //             `
  //           : ""}

  //         ${tableBodies.map((body: any) => {
  //           const [_attr, _headCols, headRows, bodyRows] = body;

  //           return html`
  //             <tbody>
  //               ${headRows.map(renderRow)}
  //               ${bodyRows.map(renderRow)}
  //             </tbody>
  //           `;
  //         })}

  //         ${tableFoot[1].length
  //           ? html`
  //               <tfoot>
  //                 ${tableFoot[1].map(renderRow)}
  //               </tfoot>
  //             `
  //           : ""}
  //       </table>
  //     </div>
  //   `;
  // }
}

customElements.define("block-renderer", BlockRenderer);